import { useMemo } from 'react';
import { type NavItemNoIcon, type SiteConfig, type SublinkItem, variant } from '@cx-web/domain-types';
import classnames from 'classnames';

import { useDesktopHeaderTracking } from '../../../../context/DesktopHeaderTrackingContext';
import { mergePersistentParams } from '../../../../utils/merge-persistent-params';
import BookieLogo, { LOGO_SIZE } from '../../../BookieLogo/BookieLogo';

import styles from './style/PromoCodeDropdown.module.css';

interface PromoCodeDropdownProps {
  bookmakers: SiteConfig['bookmakers'];
  callback?: (text: string) => void;
  label: string;
  links: SublinkItem[] | NavItemNoIcon[];
  serverUrl: string;
}

const PromoCodeDropdown = ({ bookmakers, links, serverUrl }: PromoCodeDropdownProps): React.JSX.Element | null => {
  const requestUrl = serverUrl;
  const selectedPath = new URL(serverUrl).pathname;
  const track = useDesktopHeaderTracking();

  const cleanLinks = useMemo(
    () =>
      links.map(({ bookieCode = '', icon = '', link = '', name }) => {
        const isSelected = selectedPath === link;

        return {
          icon: bookieCode || icon,
          link,
          name,
          isSelected,
        };
      }),
    [links, selectedPath],
  );

  if (!cleanLinks.length) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="promo-code-container">
      {cleanLinks.map(({ icon, isSelected, link, name = '' }) => {
        return (
          <a
            aria-current={isSelected ? 'page' : undefined}
            className={classnames(styles.promoCodeItem, isSelected ? styles.selected : '')}
            data-testid="promo-code-item"
            href={mergePersistentParams(link, requestUrl)}
            key={name}
            onClick={() => track(variant.tertiary, name)}
          >
            <BookieLogo
              bookieCode={icon}
              bookmakers={bookmakers}
              className={styles.bookieLogo}
              size={LOGO_SIZE.SMALL}
            />
            <div className={styles.promoCodeItemText}>{name}</div>
          </a>
        );
      })}
    </div>
  );
};

export default PromoCodeDropdown;
